import { AddCommaNumber } from "./Utils";

const hasValue = (value) =>
  value !== null && value !== undefined && String(value).trim() !== "";

const getParts = (value) =>
  hasValue(value)
    ? String(value)
        .split("|")
        .map((part) => part.trim())
        .filter(Boolean)
    : [];

const getStoredOptions = (value) =>
  getParts(value)
    .map((part) => {
      const [rawLabel, rawPrice] = part.split(",");
      const price = Number(rawPrice);

      return {
        label: (rawLabel || "").trim(),
        price:
          rawPrice !== undefined && Number.isFinite(price) && price > 0
            ? price
            : null,
      };
    })
    .filter(({ label }) => Boolean(label));

const getStoredLabel = (value) => getParts(value)[0]?.split(",")[0].trim() || "";

const getStoredLabels = (value) => getStoredOptions(value).map(({ label }) => label);

const getStoredPrice = (value) => getStoredOptions(value)[0]?.price || null;

const getAttributeMap = (attributes) => {
  if (!attributes) return {};
  if (typeof attributes === "object") return attributes;

  try {
    return JSON.parse(attributes) || {};
  } catch {
    return {};
  }
};

const getAttributeLabel = (attribute) => {
  const value =
    attribute && typeof attribute === "object"
      ? attribute.label || attribute.value
      : attribute;
  return hasValue(value) ? String(value).trim() : "";
};

const isPlaceholderAttribute = (value) =>
  ["no-size", "no-color", "no-material", "no_size", "no_color", "no_material"].includes(
    String(value || "").trim().toLowerCase(),
  );

const selectedFromSettings = (selectedIds, settings) => {
  if (!Array.isArray(settings)) return [];
  const ids = Array.isArray(selectedIds) ? selectedIds : [selectedIds];
  return settings.filter((setting) =>
    ids.some((id) => String(id) === String(setting.id)),
  );
};

const formatPercent = (value, label) => {
  const normalized = String(value || "").replace("%", "").trim();
  return normalized && normalized !== "100" ? `${label}: ${normalized}%` : "";
};

const getSettingPrice = (setting = {}) => {
  const promoPrice = Number(setting.promo_price);
  if (Number.isFinite(promoPrice) && promoPrice > 0) return promoPrice;

  const price = Number(setting.value ?? setting.price);
  return Number.isFinite(price) && price > 0 ? price : null;
};

const formatPricedOption = (label, price) =>
  price ? `${label} - ${AddCommaNumber(Math.round(price))}đ` : label;

const appendUnique = (tags, value) => {
  if (hasValue(value) && !tags.includes(value)) tags.push(value);
};

/**
 * Builds printable labels from persisted Order fields, ProductVariant
 * attributes, and the dynamic selection shape used by ListOrders.
 */
export const getOrderVariantTags = (item = {}) => {
  const tags = [];
  const attributes = getAttributeMap(item.variant?.attributes);
  const selectedAttributes = getAttributeMap(item.selectedAttributes);
  const selectedToppings = selectedFromSettings(
    item.toppingSelectedId,
    item.toppingSettings,
  );
  const storedToppings = getStoredOptions(item.topping);
  const showSizePrice = selectedToppings.length > 0 || storedToppings.length > 0;
  let hasSize = false;

  ["size", "color", "material"].forEach((key) => {
    const label = getAttributeLabel(attributes[key]);
    if (label && !isPlaceholderAttribute(label)) {
      appendUnique(
        tags,
        key === "size" && showSizePrice
          ? formatPricedOption(label, getSettingPrice(attributes[key]))
          : label,
      );
      if (key === "size") hasSize = true;
    }
  });

  Object.entries(selectedAttributes).forEach(([key, attribute]) => {
    if (["size", "color", "material"].includes(key)) return;
    appendUnique(tags, getAttributeLabel(attribute));
  });

  const selectedSize = selectedFromSettings(item.sizeSelectedId, item.sizeSettings)[0];
  if (selectedSize && !hasSize) {
    const sizeLabel = selectedSize.name || selectedSize.label;
    if (hasValue(sizeLabel)) {
      appendUnique(
        tags,
        showSizePrice
          ? formatPricedOption(sizeLabel, getSettingPrice(selectedSize))
          : sizeLabel,
      );
      hasSize = true;
    }
  }

  if (!hasSize) {
    const size = getStoredLabel(item.size);
    const price = getStoredPrice(item.size);
    if (size && !isPlaceholderAttribute(size)) {
      appendUnique(tags, showSizePrice ? formatPricedOption(size, price) : size);
    }
  }

  const selectedFlavors = selectedFromSettings(
    item.flavorSelectedId,
    item.flavorSettings,
  );
  if (selectedFlavors.length) {
    selectedFlavors.forEach((flavor) => {
      const flavorLabel = flavor.name || flavor.label;
      if (hasValue(flavorLabel)) appendUnique(tags, `Vị: ${flavorLabel}`);
    });
  } else {
    getStoredLabels(item.flavor).forEach((flavor) =>
      appendUnique(tags, `Vị: ${flavor.replace(/^vị\s*/i, "").trim()}`),
    );
  }

  if (selectedToppings.length) {
    selectedToppings.forEach((topping) => {
      const toppingLabel = (topping.name || topping.label || "")
        .replace(/^topping\s*/i, "")
        .trim();
      if (hasValue(toppingLabel)) {
        appendUnique(tags, formatPricedOption(toppingLabel, getSettingPrice(topping)));
      }
    });
  } else {
    storedToppings.forEach(({ label, price }) => {
      const toppingLabel = label.replace(/^topping\s*/i, "").trim();
      if (hasValue(toppingLabel)) {
        appendUnique(tags, formatPricedOption(toppingLabel, price));
      }
    });
  }

  appendUnique(
    tags,
    formatPercent(
      hasValue(item.iceSelectedValue) ? item.iceSelectedValue : item.ice,
      "Đá",
    ),
  );
  appendUnique(
    tags,
    formatPercent(
      hasValue(item.sugarSelectedValue) ? item.sugarSelectedValue : item.sugar,
      "Đường",
    ),
  );

  if (!tags.length && item.variant?.sku) appendUnique(tags, item.variant.sku);
  return tags;
};
